import moment from 'moment';

// Canonical day keys, Monday-first to match how the Settings UI lists them.
export const WORKING_DAY_KEYS = [
  'mon',
  'tue',
  'wed',
  'thu',
  'fri',
  'sat',
  'sun',
];

// moment().day() is 0 (Sun) - 6 (Sat); index into this to get the day key.
const MOMENT_DAY_INDEX_TO_KEY = [
  'sun',
  'mon',
  'tue',
  'wed',
  'thu',
  'fri',
  'sat',
];

// Default when a company hasn't configured a working schedule yet: Mon-Fri,
// Saturday/Sunday off.
export const DEFAULT_WORKING_DAYS = ['mon', 'tue', 'wed', 'thu', 'fri'];

/**
 * Whether `dateInput` falls on one of the company's configured working days.
 * @param {moment.Moment|string|Date} dateInput - anything moment() can parse.
 * @param {string[]} [workingDays] - day keys (see WORKING_DAY_KEYS); defaults
 *   to DEFAULT_WORKING_DAYS when the company hasn't set a schedule.
 */
export const isWorkingDay = (dateInput, workingDays = DEFAULT_WORKING_DAYS) => {
  const dayKey = MOMENT_DAY_INDEX_TO_KEY[moment(dateInput).day()];
  return workingDays.includes(dayKey);
};

/**
 * The company holiday landing on `dateInput`, if any.
 * @param {moment.Moment|string|Date} dateInput - anything moment() can parse.
 * @param {{date: string, name: string, optional?: boolean}[]} [holidays] - the company's holiday list for the relevant year.
 * @returns {{date: string, name: string, optional?: boolean}|null}
 */
export const getHolidayForDate = (dateInput, holidays = []) => {
  const dateKey = moment(dateInput).format('YYYY-MM-DD');
  return holidays.find((holiday) => holiday.date === dateKey) || null;
};

/**
 * Mirrors functions/attendance-utils-shared.js's mergeTeamHolidayOverrides.
 * The company list, with any date present in the team's override map
 * shadowed by that map's value (dropped if `null`, replaced if a holiday
 * object). A date the team never touched passes through unchanged.
 * @param {{date: string, name: string, optional?: boolean}[]} [companyHolidays]
 * @param {Object<string, {date: string, name: string, optional?: boolean}|null>} [overrides]
 */
export const mergeTeamHolidayOverrides = (
  companyHolidays = [],
  overrides = {}
) => {
  const byDate = new Map(
    companyHolidays.map((holiday) => [holiday.date, holiday])
  );
  Object.entries(overrides).forEach(([date, value]) => {
    if (value === null) {
      byDate.delete(date);
    } else {
      byDate.set(date, value);
    }
  });
  return [...byDate.values()];
};
